import React, { useEffect, useContext } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import moment from '@g4/moment-utc';
import { useMutation } from '@apollo/react-hooks';
import tracking from '@g4/g4-analytics';

import WrappedFlightSearchForm from './indexPage/WrappedFlightSearchForm';

import {
  withApplicationQuery,
  useTranslations,
} from '../graphql/components/ApplicationQueryProvider';

import INDEX_PAGE_TRANSLATIONS_QUERY from './indexPage/indexPageTranslations.query.graphql.js';
import USER_SUMMARY_QUERY from '../components/siteLayout/siteHeader/userMenu/userSummary.query.graphql.js';

import { Box, Layout, Media, useMedia } from '../components/Boneyard';
import { useModal } from '../components/ModalProvider';
import INFO_ICON from '../images/ic-error-dark.svg';
import ErrorBanner from './indexPage/ErrorBanner';
import HeroImage from '././indexPage/HeroImage';
import TravelAdvisory from '././indexPage/TravelAdvisory';
import EmailSignUp from '././indexPage/EmailSignUp';
import BillboardDisClaimer from './indexPage/BillboardDisclaimer';
import BillboardConfiguration from './indexPage/BillboardConfiguration';
import HowToAllegiant from './indexPage/HowToAllegiant';
import UpliftMerchandiseBanner from './indexPage/UpliftMerchandiseBanner';
import MerchandiseSpotBanner from './indexPage/MerchandiseSpotBanner';

import OverlayMerchandise from './indexPage/OverlayMerchandise';
import { useAuthentication, useSSRQuery } from '../graphql';
import OVERLAY_CHECK_QUERY from '../pageComponents/indexPage/overlayMerchandise/overlayCheck.query.graphql.js';
import OVERLAY_QUERY from '../pageComponents/indexPage/overlayMerchandise/overlay.query.graphql.js';
import LOGIN_WITH_AUTHCODE_MUTATION from '../graphql/components/authenticationProvider/loginWithAuthCode.query.graphql.js';
import Markdown from '../components/Markdown';
import HighlightedDealsSection from './indexPage/HighlightedDealsSection';
import { AllegiantStadiumBannerContext } from './app/AllegiantStadiumBannerProvider';
import { useUserContext } from '@g4/next-furniture/UserContextProvider';
import UpdatePasswordBanner from '@g4/next-furniture/UpdatePassword/UpdatePasswordBanner';
import SuccessMessageBanner from '@g4/next-furniture/EditEmailForm/SuccessMessageBanner';
import GlobalErrorPopup from '@g4/next-furniture/GlobalErrors/GlobalErrorPopup';
import useWindowLocationSearchParams from '../hooks/useWindowLocationSeachParams.js';
import PaypalBanner from './indexPage/PaypalBanner';
import LANDING_PAGE_PAYPAL_BANNER from './indexPage/paypalBanner/PaypalBanner.query.graphql.js';
import INITIAL_DATA_QUERY from './indexPage/wrappedFlightSearchForm/initialData.query.graphql';
import { hasTargetingConsent } from '../utils/ga4Utils.js';
import useStickyUtmCapture from '../hooks/useStickyUtmCapture';

const BackgroundContainer = styled.div`
  position: relative;
  padding: 0;
  ${props => props.theme.query.md} {
    padding: ${props => props.theme.spacing(20)} 0 0;
  }
`;

const FormWrapper = styled.div`
  position: relative;
  width: 100%;
  background: linear-gradient(
    ${props => props.theme.colors.flightSearchForm.background.from},
    ${props => props.theme.colors.flightSearchForm.background.to} 40%
  );
  padding: ${props => props.theme.spacing(8)} ${props => props.theme.spacing(3)};
  margin-top: ${props => (props.offsetTopPosition ? 44 : 0)}px;
  border-radius: 8px;

  ${props => props.theme.query.xs} {
    width: calc(100% + ${props => props.theme.spacing(6)});
    padding: ${props => props.theme.spacing(4)};
    margin-top: -1px;
    margin-left: -${props => props.theme.spacing(3)};
    ${props =>
      props.isSunCountryEnabled && props.imageMobile && {
        backgroundImage: `url(${props.imageMobile})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
      }}
  }

  ${(props) => props.theme.query.md} {
    width: 100%;
    padding: ${(props) => props.theme.spacing(8)};
    margin-top: ${(props) => (props.offsetTopPosition ? 44 : 0)}px;
    margin-left: 0;
    margin-bottom: ${(props) => props.theme.spacing(12)};
    border-radius: 8px;
    ${props =>
      props.isSunCountryEnabled && props.imageIPad && {
        backgroundImage: `url(${props.imageIPad})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
      }}
  }

  ${props => props.theme.query.lg} {
    width: 100%;
    padding: ${props => props.theme.spacing(8)};
    margin-top: ${props => (props.offsetTopPosition ? 44 : 0)}px;
    margin-left: 0;
    margin-bottom: ${props => props.theme.spacing(12)};
    border-radius: 8px;
    ${props =>
      props.isSunCountryEnabled && props.imageWeb && {
        backgroundImage: `url(${props.imageWeb})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
  }

  @media (min-width: 575px) and (max-width: 750px) {
    ${(props) =>
      props.isSunCountryEnabled && props.imageIPad && {
        backgroundImage: `url(${props.imageIPad})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
      }}
  }
`;

const BrandingBannerContainer = styled.div`
  width: 100%;
  position: relative;
  margin-left: 0px;
  z-index: 1;
`;

const BrandingBannerWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 16vw;
  background: url(${props => props.imageMobile}) no-repeat center center /
    100%;
  z-index: 1;

  ${props => props.theme.query.md} {
    position: absolute;
    height: 5em;
    background: url(${props => props.imageWeb}) no-repeat left center / 70%;
    padding: ${props => props.theme.spacing(2)};
    top: 10px;
    left: -10px;
  }

  @media (min-width: 750px) and (max-width: 1000px) {
    background: url(${props => props.imageMobile}) no-repeat left center / 60%;
  }

  @media (min-width: 1000px) {
    background-size: 70%;
    left: -4px;
  }

  @media (min-width: 1200px) {
    background-size: 75%;
  }

  @media (min-width: 1300px) {
    background-size: 80%;
  }

  @media (min-width: 1525px) {
    background-size: 90%;
  }
`;

const Tab = styled.div`
  color: ${props => props.theme.colors.flightSearchForm.textColor};
  width: 100%;
  text-align: center;
  margin-bottom: ${props => props.theme.spacing(4)};
`;

const CarouselWrapper = styled.div`
  position: relative;
`;

const FullWidthContainer = styled(Layout.FullWidth)`
  background: white;
`;

const IndexPage = () => {
  const { push } = useRouter();
  const { isAuthenticated } = useAuthentication();

  // Get user summary data for loyaltyId
  const { data: userSummaryData, loading: userSummaryLoading } = useSSRQuery(
    USER_SUMMARY_QUERY,
    {
      skip: !isAuthenticated,
    }
  );

  const loyaltyId = userSummaryData?.viewer?.loyaltyId;

  useEffect(() => {
    if (!userSummaryLoading) {
      const allwaysAccountNo =
        isAuthenticated && hasTargetingConsent() ? loyaltyId : '';
      
      tracking.setDataLayerVars({
        customer_type: isAuthenticated ? 'Customer' : 'Guest',
        allways_account_no: allwaysAccountNo,
      });
      tracking.trackPageView('', 'page_view');
    }
  }, [isAuthenticated, loyaltyId, userSummaryLoading]);

  const { 
  isImpersonation,
   isTravelAgent, 
   isStationAgent, 
   isCallCenterAgent,
   agentType,
   } = useUserContext();
  const hasAgentBanner = !!agentType && !isTravelAgent;
  const isNotAgent = !isTravelAgent && !isStationAgent && !isCallCenterAgent;
  const { setShowModal, setContent, setTitle, unSetModal } = useModal();
  const mq = useMedia();
  const isMobile = mq.lte('sm');

  const translations = useTranslations();

  const [heroImage, setHeroImage] = React.useState();
  const [currentDisclaimer, setCurrentDisclaimer] = React.useState();
  const [currentOrigin, setCurrentOrigin] = React.useState();
  const [currentNewDestination, setCurrentDestination] = React.useState();
  const [isFlightMarketError, setIsFlightMarketError] = React.useState(false);
  const [
    isOverlayMerchandiseOverTimeLimit,
    setIsOverlayMerchandiseOverTimeLimit,
  ] = React.useState(false);
  const { stadiumBannerData } = useContext(AllegiantStadiumBannerContext) || {};
  const { allegiantStadiumBannerData } = stadiumBannerData || {};
  const queryParams = useWindowLocationSearchParams();

  const showErrorPopup = () => {
    unSetModal();
    setContent(
      <GlobalErrorPopup isMobile={isMobile} closePopup={unSetModal} />
    );
    setTitle('');
    setShowModal(true);
  };

  const [loginWithAuthCode] = useMutation(LOGIN_WITH_AUTHCODE_MUTATION, {
    onCompleted(data) {
      const token = data?.loginWithAuthCode?.token;
      if (token) {
        const forceResetPasswordFlag =
          data.loginWithAuthCode.viewer.forceResetPassword;
        if (
          forceResetPasswordFlag &&
          (window.location.pathname === '/home' ||
            window.location.pathname === '/')
        ) {
          push(`/updatepassword?id=${data.loginWithAuthCode.viewer.id}`);
        } else if (queryParams.emailUpdated) {
          push('/?email_updated=true');
        }
      } else if (data?.loginWithAuthCode?.errors?.length > 0) {
        if (data?.loginWithAuthCode?.errors[0] === 'SERVICE_UNAVAILABLE') {
          showErrorPopup();
          return;
        }
        push(`/?error=true`);
      } else {
        push('/');
      }
    },
  });

  const authCode = queryParams?.code;

  useEffect(() => {
    if (authCode) {
      loginWithAuthCode({
        variables: {
          authCode: authCode,
          application: 'MYALLEGIANT',
        },
      });
    }
  }, [authCode, loginWithAuthCode]);
  const router = useRouter();

  const checkOverlays = () => {
    const savedTime =
      typeof window === 'object' &&
      window.localStorage.getItem('overlayMerchandiseDisplayExp');
    const timeDiff = moment().diff(moment(savedTime), 'hours');
    let isOverTimeLimit = false;

    if (!savedTime || timeDiff >= 0) {
      const twentyFourHoursAhead = moment().add(24, 'hours');
      typeof window === 'object' &&
        window.localStorage.setItem(
          'overlayMerchandiseDisplayExp',
          twentyFourHoursAhead.toString()
        );
      isOverTimeLimit = true;
    }

    setIsOverlayMerchandiseOverTimeLimit(isOverTimeLimit);
  };

  useEffect(() => {
    checkOverlays();
    // handle issue when react re-runs the useEffect immediately
    return () => {
      const twentyFourHoursAhead = moment().add(24, 'hours');
      const savedTime = window.localStorage.getItem(
        'overlayMerchandiseDisplayExp'
      );
      const diff = twentyFourHoursAhead.diff(moment(savedTime), 'seconds');
      const REACT_UNMOUNT_CHECK_INTERVAL = 2; // two seconds
      if (diff < REACT_UNMOUNT_CHECK_INTERVAL) {
        // react is re-running the use effect within a couple of seconds
        window.localStorage.removeItem('overlayMerchandiseDisplayExp');
      }
    };
  }, [setIsOverlayMerchandiseOverTimeLimit]);

  const updateOrigin = orig => {
    setCurrentOrigin(orig);
  };

  const updateDestination = dest => {
    setCurrentDestination(dest);
  };

  const overlays = useSSRQuery(OVERLAY_CHECK_QUERY);

  const paypalBannerData = useSSRQuery(LANDING_PAGE_PAYPAL_BANNER);
  const initialDataResponse = useSSRQuery(INITIAL_DATA_QUERY, { variables: { isBooking: true } });

  const { loading, error, data, refetch } = useSSRQuery(OVERLAY_QUERY, {
    fetchPolicy: 'network-only',
  });

  // persists a utm_source in cookie so it survives the shopping session being recreated
  const {
    stickyUtmSources,
    stickyUtmTtlSeconds,
  } = initialDataResponse?.data?.application?.configurations ?? {};

  useStickyUtmCapture({
    utmSource: queryParams?.utm_source,
    isConfigReady: !initialDataResponse.loading && !initialDataResponse.error,
    stickyUtmSources,
    stickyUtmTtlSeconds,
  });

  React.useEffect(() => {
    refetch && refetch();
  }, [isAuthenticated, refetch]);

  if (loading || initialDataResponse.loading) {
    return null;
  }

  if (error || initialDataResponse.error) {
    throw error;
  }

  // if travel agent is not logged in then redirect TA home page to /ta-login
  const isTravelAgentHomePage = () => {
    const hostname =
      typeof window !== 'undefined' ? window.location.hostname : '';
    return hostname.startsWith('ta.') || hostname.startsWith('ta-');
  };

  if (isTravelAgentHomePage() && !isTravelAgent) {
    router.replace('/ta-login');
    return null;
  }

  const fetchOverlay = data?.application.merchandiseOverlay;

  const displayOverlay =
    overlays.data?.application.configurations.merchandiseOverlayDisplay &&
    !isImpersonation;

  const allegiantStadiumBanner =
    allegiantStadiumBannerData?.application?.merchandiseBanner;

  const paypalEnabled =
    paypalBannerData?.data?.application?.configurations?.paypalEnabled;
  const paypalBannersEnabled =
    paypalBannerData?.data?.application?.configurations?.paypalBannersEnabled;

  const paypalLogo = paypalBannerData?.data?.application?.images?.find(
    image => image.id === 'paypalLogo'
  );

  const isSunCountryEnabled =
    initialDataResponse?.data?.application?.configurations?.isSunCountryEnabled;

  const sunCountryImagesList = initialDataResponse?.data?.application?.images;
  const getSunCountryImage = id => sunCountryImagesList?.find(image => image.id === id)?.path;

  return (
    <Media>
      {mq => (
        <React.Fragment>
          <TravelAdvisory
            isFlightMarketError={isFlightMarketError}
            hasAgentBanner={hasAgentBanner}
            isPasswordUpdated={queryParams?.password_updated}
            isEmailUpdated={queryParams?.email_updated}
            isError={queryParams?.token_error || queryParams?.error}
          />
          {isFlightMarketError && (
            <ErrorBanner
              icon={INFO_ICON}
              ln1={setIsFlightMarketError}
              offsetTopPosition={Boolean(allegiantStadiumBanner)}
            />
          )}
          {(queryParams?.token_error || queryParams?.error) && (
            <ErrorBanner
              icon={INFO_ICON}
              errorMessage={translations.resetPassword.tokenExpiredMessage}
            />
          )}
          {queryParams?.password_updated && <UpdatePasswordBanner />}
          {queryParams?.email_updated && <SuccessMessageBanner />}
          {isOverlayMerchandiseOverTimeLimit &&
            isNotAgent &&
            displayOverlay &&
            fetchOverlay.content && (
              <>
                <Markdown
                  source={fetchOverlay.content}
                  customElements={{ OverlayMerchandise }}
                />
              </>
            )}
          {isSunCountryEnabled && (
            <BrandingBannerContainer>
              <Box position="relative">
                <BrandingBannerWrapper
                  imageWeb={getSunCountryImage('sunCountryTopBanner')}
                  imageMobile={getSunCountryImage('mobileSunCountryTopBanner')}
                />
              </Box>
            </BrandingBannerContainer>
          )}
          <BackgroundContainer>
            {mq.gte('md') && <HeroImage image={heroImage} />}
            <FormWrapper
              offsetTopPosition={Boolean(allegiantStadiumBanner)}
              imageWeb={getSunCountryImage('sunCountrySearchBanner')}
              imageMobile={getSunCountryImage('mobileSunCountrySearchBanner')}
              imageIPad={getSunCountryImage('ipadSunCountrySearchBanner')}
              isSunCountryEnabled={isSunCountryEnabled}
            >
              {mq.lt('md') && (
                <Tab>{translations.flightSearchForm.flightSearchFormTitle}</Tab>
              )}
              <WrappedFlightSearchForm
                onOriginChange={updateOrigin}
                onNewDestinationChange={updateDestination}
                searchResultsPath="/booking/flights"
                displayFormFooter
                setIsFlightMarketError={setIsFlightMarketError}
                initialDataResponse={initialDataResponse}
              />
            </FormWrapper>
            <EmailSignUp />
            <CarouselWrapper>
              <BillboardConfiguration
                updateHeroImage={setHeroImage}
                updateDisclaimer={setCurrentDisclaimer}
                selectedOrigin={currentOrigin}
                selectedDestination={currentNewDestination}
                isMobile={!mq.gte('md')}
              />
            </CarouselWrapper>
            <PaypalBanner
              logo={paypalLogo}
              disabled={!(paypalEnabled && paypalBannersEnabled && isNotAgent)}
            />
          </BackgroundContainer>
          <Box position="relative">
            <UpliftMerchandiseBanner />
            <MerchandiseSpotBanner />
            <FullWidthContainer>
              <HighlightedDealsSection />
              <HowToAllegiant />
              {currentDisclaimer && (
                <BillboardDisClaimer disclaimerText={currentDisclaimer} />
              )}
            </FullWidthContainer>
          </Box>
        </React.Fragment>
      )}
    </Media>
  );
};

export default withApplicationQuery(INDEX_PAGE_TRANSLATIONS_QUERY, IndexPage);
